import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  LayoutDashboard,
  LineChart,
  FileText,
  List,
  Settings,
  LogOut,
  House,
  User,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import { logoutUser } from '../../../../services/session';
import { useAuthSession } from '../../../../contexts/AuthSessionContext';
import { PRODUCT_IMAGES } from '../../../../branding/assets';

const Sidebar = ({ open = true, mobileOpen = false, isDesktop = true, onNavigate, onToggle }) => {
  const mini = isDesktop ? !open : false;
  const navigate = useNavigate();
  const { user } = useAuthSession();
  const userName = user?.name || user?.username || 'Usuário';
  const [tooltip, setTooltip] = useState({
    visible: false,
    text: '',
    top: 0,
    left: 0,
  });
  const [toggleHovered, setToggleHovered] = useState(false);
  const [backHovered, setBackHovered] = useState(false);
  const [logoutHovered, setLogoutHovered] = useState(false);
  const navItems = [
    { path: '/feedback-hero', name: 'Painel', icon: <LayoutDashboard size={22} />, exact: true },
    { path: '/feedback-hero/comparativo', name: 'Evolução', icon: <LineChart size={22} /> },
    { path: '/feedback-hero/form', name: 'Formulário', icon: <FileText size={22} /> },
    { path: '/feedback-hero/respostas', name: 'Respostas', icon: <List size={22} /> },
    { path: '/feedback-hero/configuracoes', name: 'Configurações', icon: <Settings size={22} /> },
  ];

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  const handleBack = () => {
    navigate('/products');
  };

  const handleTooltipShow = (label, target) => {
    if (!mini || !target) return;
    const rect = target.getBoundingClientRect();
    setTooltip({
      visible: true,
      text: label,
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
  };

  const handleTooltipHide = () => {
    if (!tooltip.visible) return;
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    setToggleHovered(false);
    setBackHovered(false);
    setLogoutHovered(false);
  }, [open, mobileOpen]);

  const handleToggleClick = () => {
    setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    setToggleHovered(false);
    onToggle?.();
  };

  const toggleLabel = !isDesktop
    ? 'Fechar menu'
    : open
      ? 'Recolher menu'
      : 'Expandir menu';

  return (
    <aside
      className={`
        fixed top-0 left-0 z-40 flex h-screen w-64 flex-col bg-[#153023] border-r border-gray-200
        transition-all duration-300 ease-in-out
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        ${mini ? 'md:w-[4.5rem] md:min-w-[4.5rem] md:max-w-[4.5rem]' : 'md:w-64 md:min-w-[16rem] md:max-w-[16rem]'}
        md:static md:translate-x-0
        overflow-x-hidden
      `}
    >
      <div
        className={`flex flex-col w-full ${mini ? 'pt-3 pb-2' : 'pt-3 pb-3'}`}
      >
        <div
          className={`relative flex w-full items-center ${
            mini ? 'flex-col gap-2 px-1' : 'justify-between px-3'
          }`}
          style={!mini ? { minHeight: '6rem' } : undefined}
        >
          {!mini && (
            <img
              src={PRODUCT_IMAGES.feedbackHero}
              alt="Feedback Hero"
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-1/2 w-auto -translate-x-1/2 -translate-y-1/2 transition-all duration-300"
              style={{ height: '6rem' }}
            />
          )}
          {!mini && (
            <div
              className="relative z-10 shrink-0"
              onMouseEnter={() => setBackHovered(true)}
              onMouseLeave={() => setBackHovered(false)}
            >
              <button
                type="button"
                onClick={handleBack}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#01E169]/15 text-[#01E169] ring-1 ring-[#01E169]/30 transition-all hover:bg-[#01E169]/25 hover:text-white hover:ring-[#01E169]/60"
                aria-label="Voltar para produtos"
              >
                <House size={22} strokeWidth={2.2} />
              </button>
              {backHovered && (
                <span className="pointer-events-none absolute top-1/2 left-full ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-lg ring-1 ring-white/10">
                  Voltar para produtos
                </span>
              )}
            </div>
          )}
          {mini && onToggle && (
            <button
              type="button"
              onClick={handleToggleClick}
              aria-label={toggleLabel}
              onMouseEnter={(event) => handleTooltipShow(toggleLabel, event.currentTarget)}
              onMouseLeave={handleTooltipHide}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#01E169]/30 bg-[#01E169]/10 text-[#D1FADF] transition-colors hover:bg-[#01E169]/25 hover:text-white"
            >
              <ChevronRight size={18} />
            </button>
          )}
          {!mini && onToggle && (
            <div
              className="relative z-10 shrink-0"
              onMouseEnter={() => setToggleHovered(true)}
              onMouseLeave={() => setToggleHovered(false)}
            >
              <button
                type="button"
                onClick={handleToggleClick}
                aria-label={toggleLabel}
                className={`flex items-center justify-center rounded-full border border-[#01E169]/30 bg-[#01E169]/10 text-[#D1FADF] transition-colors hover:bg-[#01E169]/25 hover:text-white ${
                  !isDesktop ? 'h-11 w-11' : 'h-9 w-9'
                }`}
              >
                {!isDesktop ? <X size={22} /> : <ChevronLeft size={18} />}
              </button>
              {toggleHovered && isDesktop && (
                <span className="pointer-events-none absolute top-1/2 right-full mr-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-lg ring-1 ring-white/10">
                  {toggleLabel}
                </span>
              )}
            </div>
          )}
        </div>
        {mini && (
          <img
            src={PRODUCT_IMAGES.feedbackHero}
            alt="Feedback Hero"
            className="w-auto self-center mt-4 transition-all duration-300"
            style={{ height: '3rem' }}
          />
        )}
      </div>
      <nav
        className={`flex-1 overflow-y-auto overflow-x-hidden ${mini ? 'px-1' : 'px-2'} pt-2 w-full`}
      >
        <ul className="space-y-1 w-full">
          {navItems.map((item) => (
            <li key={item.path} className="relative w-full">
              <NavLink
                to={item.path}
                end={item.exact}
                onClick={() => {
                  onNavigate?.();
                  handleTooltipHide();
                }}
                onMouseEnter={(event) => handleTooltipShow(item.name, event.currentTarget)}
                onMouseLeave={handleTooltipHide}
                className={({ isActive }) =>
                  `flex items-center ${mini ? 'justify-center' : ''} px-2 py-3 text-sm font-medium rounded-lg transition-colors duration-200 w-full ${
                    isActive
                      ? 'bg-[#01E169] text-[#153023] font-semibold'
                      : 'text-[#D1FADF] hover:bg-[#01E169]/20 hover:text-[#01E169]'
                    }`
                }
              >
                <span className="mr-0">{item.icon}</span>
                {!mini && <span className="ml-3">{item.name}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div
        className={`mt-auto w-full border-t border-[#01E169]/15 ${
          mini ? 'flex flex-col items-center px-1 py-3' : 'flex items-center gap-2 px-3 py-3'
        }`}
      >
        {!mini && (
          <>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#01E169]/15 text-[#D1FADF]">
              <User size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-white capitalize">{userName}</div>
            </div>
          </>
        )}
        {mini ? (
          <button
            type="button"
            onClick={handleLogout}
            onMouseEnter={(event) => handleTooltipShow('Sair', event.currentTarget)}
            onMouseLeave={handleTooltipHide}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-[#D1FADF] transition-colors hover:bg-[#01E169]/15 hover:text-[#01E169]"
            aria-label="Sair"
          >
            <LogOut size={18} />
          </button>
        ) : (
          <div
            className="relative shrink-0"
            onMouseEnter={() => setLogoutHovered(true)}
            onMouseLeave={() => setLogoutHovered(false)}
          >
            <button
              type="button"
              onClick={handleLogout}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-[#D1FADF] transition-colors hover:bg-[#01E169]/15 hover:text-[#01E169]"
              aria-label="Sair"
            >
              <LogOut size={18} />
            </button>
            {logoutHovered && (
              <span className="pointer-events-none absolute top-1/2 right-full mr-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-lg ring-1 ring-white/10">
                Sair
              </span>
            )}
          </div>
        )}
      </div>
      {mini && tooltip.visible && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed px-3 py-1.5 rounded-md text-[11px] font-semibold tracking-wide bg-slate-900/95 text-white whitespace-nowrap z-[220] shadow-xl ring-1 ring-white/10 backdrop-blur-sm pointer-events-none"
          style={{ top: tooltip.top, left: tooltip.left, transform: 'translateY(-50%)' }}
        >
          {tooltip.text}
        </div>,
        document.body
      )}
    </aside>
  );
};

export default Sidebar;
